import { useState, type SubmitEventHandler } from 'react';
import { toast } from 'sonner';
import type { Bookmark, UpdateBookmark } from '@stashmark/shared';
import { isValidUrl } from '@stashmark/shared';
import { useUpdateBookmarkMutation } from '@/api/bookmarks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type ContentProps = {
  bookmark: Bookmark;
  onClose: () => void;
};

type Props = {
  bookmark: Bookmark | null;
  isOpen: boolean;
  onClose: () => void;
};

const EditBookmarkDialogContent = ({ bookmark, onClose }: ContentProps) => {
  const [url, setUrl] = useState(bookmark.url ?? '');
  const [title, setTitle] = useState(bookmark.title ?? '');
  const [description, setDescription] = useState(bookmark.description ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  const [updateBookmark, { isLoading }] = useUpdateBookmarkMutation();

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void (async () => {
      setValidationError(null);

      if (url && !isValidUrl(url)) {
        setValidationError('Please enter a valid URL');
        return;
      }

      const updateData: UpdateBookmark = {};
      if (url !== bookmark.url) {
        updateData.url = url;
      }
      if (title !== (bookmark.title ?? '')) {
        updateData.title = title || null;
      }
      if (description !== (bookmark.description ?? '')) {
        updateData.description = description || null;
      }

      if (Object.keys(updateData).length === 0) {
        onClose();
        return;
      }

      try {
        await updateBookmark({ id: bookmark.id, data: updateData }).unwrap();
        toast.success('Bookmark updated successfully');
        onClose();
      } catch (err) {
        toast.error('Failed to update bookmark', {
          description: err instanceof Error ? err.message : 'Please try again',
        });
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="edit-url">URL</Label>
        <Input type="url" id="edit-url" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-title">Title</Label>
        <Input
          type="text"
          id="edit-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Leave empty to clear"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-description">Description</Label>
        <Textarea
          id="edit-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Leave empty to clear"
          rows={3}
        />
      </div>

      {validationError && (
        <p className="text-sm text-destructive" role="alert">
          {validationError}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const EditBookmarkDialog = ({ bookmark, isOpen, onClose }: Props) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Bookmark</DialogTitle>
          <DialogDescription>Update the details of your bookmark.</DialogDescription>
        </DialogHeader>
        {bookmark && (
          <EditBookmarkDialogContent key={bookmark.id} bookmark={bookmark} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};
